import { Component, OnInit, ChangeDetectionStrategy, Inject } from '@angular/core'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import type { PrintData, PrintRowModel, PrintSettings, SectionModel } from '@app/shared/models/print.model'
import { tap } from 'rxjs/operators'
import { PrintHttpService } from '@app/shared/services/print/print.http.service'
import { LoadingService } from '@app/core'
import { Observable } from 'rxjs'
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { MatTableDataSource } from '@angular/material/table'
import { SelectionModel } from '@angular/cdk/collections'

export interface PrintDialogData {
  projectId: string
  ahuId: string
}

@UntilDestroy()
@Component({
  selector: 'app-print-dialog',
  templateUrl: './print-dialog.component.html',
  styleUrls: ['./print-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PrintDialogComponent implements OnInit {
  printSettings: PrintSettings

  isLoading$: Observable<boolean>

  form: UntypedFormGroup

  dataSource: MatTableDataSource<PrintRowModel>

  tableSelection: SelectionModel<PrintRowModel>

  DISABLE_CONTROL = 1

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: PrintDialogData,
    private printService: PrintHttpService,
    private dialogRef: MatDialogRef<PrintDialogComponent>,
    private loadingService: LoadingService,
    formBuilder: UntypedFormBuilder,
  ) {
    this.isLoading$ = this.loadingService.isLoading$

    this.form = formBuilder.group({
      outputTypes: 0,
      isSimulationReport: false,
      printSections: formBuilder.group({}),
      ahus: formBuilder.array([]),
    })
  }

  ngOnInit(): void {
    this.printService
      .getPrintSettings(this.data.projectId, this.data.ahuId)
      .pipe(
        tap({
          next: results => {
            this.printSettings = results
            this.buildForm(results)
            this.buildTable(results)
            this.form.patchValue({ outputTypes: results.OutputType })
            this.form.patchValue({ isSimulationReport: results.IsSimulationReport })
          },
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  submit(): void {
    const { outputTypes, isSimulationReport, printSections } = this.form.getRawValue()

    const data = {
      OutputType: outputTypes,
      IsSimulationReport: isSimulationReport,
      SectionValues: this.printSettings.Sections.filter(section => printSections[section.TitleTextId]).map(section => section.Value),
      AhuIds: this.tableSelection.selected.map(ahu => ahu.AhuId),
    } as PrintData

    this.dialogRef.close({ fileName: this.printSettings.FileTitle, data })
  }

  cancel(): void {
    this.dialogRef.close()
  }

  buildForm = (settings: PrintSettings): void => {
    this.form
      .get('outputTypes')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(value =>
        this.toggleDisabled(
          value === this.DISABLE_CONTROL,
          this.form.get('printSections') as UntypedFormGroup,
          this.printSettings.Sections,
        ),
      )

    this.generatePrintSections(settings.Sections)
  }

  generatePrintSections = (models: SectionModel[]): void => {
    const formArray = this.form.get('printSections') as UntypedFormGroup

    models.forEach(model => {
      formArray.addControl(model.TitleTextId, new UntypedFormControl({ value: model.Selected, disabled: !model.Enabled }))
    })
  }

  buildTable = (settings: PrintSettings): void => {
    this.dataSource = new MatTableDataSource(settings.Ahus)
    this.tableSelection = new SelectionModel(true, [...settings.Ahus.filter(ahu => ahu.Selected)])
  }

  toggleRowSelection(row: PrintRowModel): void {
    if (!row.CanSelect) {
      return
    }

    this.tableSelection.toggle(row)
  }

  toggleAllSelections(): void {
    if (this.isAllSelected) {
      this.tableSelection.clear()
    } else {
      this.tableSelection.select(...this.dataSource.data.filter(ahu => ahu.CanSelect))
    }
  }

  get isAllSelected(): boolean {
    return this.tableSelection.selected.length === this.dataSource.data.filter(ahu => ahu.CanSelect).length
  }

  get printSectionKeys(): string[] {
    return Object.keys((this.form.get('printSections') as UntypedFormGroup).controls)
  }

  private toggleDisabled = (disable: boolean, printSections: UntypedFormGroup, sections: SectionModel[]): void => {
    sections.forEach(section => {
      if (!section.Enabled) {
        return
      }
      const control = printSections.controls[section.TitleTextId]
      if (disable) {
        control.disable()
      } else {
        control.enable()
      }
    })
  }
}
